import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type SortingState,
    type ColumnFiltersState,
    type VisibilityState,
} from '@tanstack/react-table'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Download, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuPortal,
} from '@/components/ui/dropdown-menu'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    searchKey?: string
    searchPlaceholder?: string
    searchValue?: string // Server-side search value
    onSearchChange?: (value: string) => void // Server-side search callback
    onExport?: () => void
    exportButtonText?: string
    className?: string
    toolbarContent?: React.ReactNode // Custom content to render next to search field
    // Server-side pagination props
    pagination?: {
        page: number
        limit: number
        total: number
        onPageChange: (page: number) => void
        onPageSizeChange: (limit: number) => void
    }
    onRowSelectionChange?: (rows: TData[]) => void
    enableRowSelection?: boolean
    enableSorting?: boolean
    enableFilters?: boolean
    disablePagination?: boolean // New prop to disable pagination
    onRowClick?: (row: TData) => void // Handler for row clicks
    getRowClassName?: (row: TData) => string // Optional row class names based on row data
    getCellClassName?: (row: TData, columnId: string) => string // Optional cell class names based on row and column
    firstHeaderCellClassName?: string // Optional class for first header cell (e.g. grey border to match header when body has colored border)
}

export function DataTable<TData, TValue>({
    columns,
    data,
    searchKey,
    searchPlaceholder = 'Search...',
    searchValue,
    onSearchChange,
    onExport,
    exportButtonText,
    className,
    toolbarContent,
    pagination,
    onRowSelectionChange,
    enableRowSelection = false,
    enableSorting = true,
    enableFilters = true,
    disablePagination = false,
    onRowClick,
    getRowClassName,
    getCellClassName,
    firstHeaderCellClassName,
}: DataTableProps<TData, TValue>) {
    const { t, ready, i18n } = useTranslation()
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = useState({})
    const [viewDropdownOpen, setViewDropdownOpen] = useState(false)

    // Memoized translations to prevent excessive re-computation
    const translations = useMemo(() => {
        const getTranslation = (key: string, fallback: string) => {
            // Check if i18n is ready and working properly
            if (ready && typeof t === 'function' && i18n?.hasResourceBundle(i18n?.language || 'en-US', 'translation')) {
                try {
                    // Check if the specific key exists before calling t()
                    if (i18n.exists(key)) {
                        const translation = t(key)
                        // If translation returns the key itself, it means the key wasn't found
                        if (translation !== key && translation && translation.trim() !== '') {
                            return translation
                        }
                    }
                } catch (error) {
                    // Silently fall back to default text
                }
            }
            return fallback
        }

        return {
            goToFirstPage: getTranslation('common.goToFirstPage', 'Go to first page'),
            goToPreviousPage: getTranslation('common.goToPreviousPage', 'Go to previous page'),
            goToNextPage: getTranslation('common.goToNextPage', 'Go to next page'),
            goToLastPage: getTranslation('common.goToLastPage', 'Go to last page'),
            export: getTranslation('common.export', 'Export'),
            view: getTranslation('common.view', 'View'),
            noResults: getTranslation('common.noResults', 'No results.'),
            rowsPerPage: getTranslation('common.rowsPerPage', 'Rows per page'),
            page: getTranslation('common.page', 'Page'),
            of: getTranslation('common.of', 'of')
        }
    }, [ready, t, i18n])

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: (updater) => {
            setRowSelection(updater)
            if (onRowSelectionChange) {
                const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater
                const selectedRows = data.filter((_, index) => newSelection[index])
                onRowSelectionChange(selectedRows)
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: (pagination || disablePagination) ? undefined : getPaginationRowModel(),
        getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
        getFilteredRowModel: enableFilters ? getFilteredRowModel() : undefined,
        onColumnVisibilityChange: setColumnVisibility,
        enableRowSelection,
        enableSorting,
        enableFilters,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        initialState: disablePagination ? { pagination: { pageSize: 10000 } } : undefined,
        // Server-side pagination
        manualPagination: !!pagination,
        pageCount: pagination ? Math.ceil(pagination.total / pagination.limit) : undefined,
    })


    return (
        <div className={cn('space-y-4', className)}>
            {/* Toolbar - overflow-visible so filter borders/focus rings are not cropped; items-end aligns search/buttons with bottom of filter dropdowns */}
            <div className="flex items-end justify-between overflow-visible py-1">
                <div className="flex items-end space-x-2 flex-nowrap min-w-0 flex-1 overflow-visible pr-1">
                    {searchKey && (
                        <Input
                            placeholder={searchPlaceholder}
                            value={
                                pagination && onSearchChange
                                    ? (searchValue ?? '')
                                    : ((table.getColumn(searchKey)?.getFilterValue() as string) ?? '')
                            }
                            onChange={(event) => {
                                const value = event.target.value
                                if (pagination && onSearchChange) {
                                    // Server-side search
                                    onSearchChange(value)
                                } else {
                                    // Client-side search
                                    table.getColumn(searchKey)?.setFilterValue(value)
                                }
                            }}
                            className="w-52 max-w-xs flex-shrink-0"
                        />
                    )}
                    {toolbarContent && (
                        <div className="flex items-end min-w-0 overflow-visible gap-2">
                            {toolbarContent}
                        </div>
                    )}
                </div>
                <div className="flex items-end space-x-2">
                    {onExport && (
                        <Button variant="outline" size="sm" onClick={onExport}>
                            <Download className="mr-2 h-4 w-4" />
                            {exportButtonText || translations.export}
                        </Button>
                    )}
                    <DropdownMenu modal={true} open={viewDropdownOpen} onOpenChange={setViewDropdownOpen}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                onMouseDown={(e) => {
                                    // Prevent parent row click handlers from swallowing the event
                                    e.stopPropagation()
                                    e.preventDefault()
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    setViewDropdownOpen(true)
                                }}
                            >
                                <Filter className="mr-2 h-4 w-4" />
                                {translations.view}
                                <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            sideOffset={6}
                            collisionPadding={12}
                            className="z-[2000]"
                            onCloseAutoFocus={(e) => {
                                e.preventDefault()
                            }}
                        >
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    // Translate column ID using the same keys as table headers
                                    const getColumnLabel = (columnId: string) => {
                                    // Check if i18n is ready
                                    if (ready && typeof t === 'function' && i18n?.hasResourceBundle(i18n?.language || 'en-US', 'translation')) {
                                        // Special case: description column uses descriptionColumn key
                                        if (columnId === 'description') {
                                            // Try descriptionColumn first (used in roles table header)
                                            if (i18n.exists('roles.descriptionColumn')) {
                                                const translation = t('roles.descriptionColumn')
                                                if (translation !== 'roles.descriptionColumn' && translation && translation.trim() !== '') {
                                                    return translation
                                                }
                                            }
                                            // Fallback to regular description key
                                            const patterns = ['roles.description', 'users.description', 'jobs.description']
                                            for (const pattern of patterns) {
                                                if (i18n.exists(pattern)) {
                                                    const translation = t(pattern)
                                                    if (translation !== pattern && translation && translation.trim() !== '') {
                                                        return translation
                                                    }
                                                }
                                            }
                                        } else {
                                            // Try common translation key patterns (roles, users, jobs, common)
                                            const patterns = [
                                                `roles.${columnId}`,
                                                `users.${columnId}`,
                                                `jobs.${columnId}`,
                                                `common.${columnId}`
                                            ]

                                            for (const pattern of patterns) {
                                                if (i18n.exists(pattern)) {
                                                    const translation = t(pattern)
                                                    if (translation !== pattern && translation && translation.trim() !== '') {
                                                        return translation
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    // Fallback to capitalized column ID
                                    return columnId.charAt(0).toUpperCase() + columnId.slice(1)
                                }

                                return (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        className="capitalize"
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(value) =>
                                            column.toggleVisibility(!!value)
                                        }
                                    >
                                        {getColumnLabel(column.id)}
                                    </DropdownMenuCheckboxItem>
                                )
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-md border w-full min-w-full overflow-hidden">
                <Table className="w-full min-w-full">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="bg-table-header border-b border-table-border">
                                {headerGroup.headers.map((header, headerIndex) => {
                                        const headerMeta = (header.column.columnDef as { meta?: { compact?: boolean } }).meta
                                        const headerCompact = headerMeta?.compact === true
                                        const headerPad = headerCompact ? 4 : undefined
                                        return (
                                        <TableHead 
                                            key={header.id} 
                                            className={cn(
                                                "font-semibold text-foreground",
                                                headerIndex === 0 && firstHeaderCellClassName
                                            )}
                                            style={{ 
                                                paddingTop: headerPad ?? 'var(--table-header-padding-y)',
                                                paddingBottom: headerPad ?? 'var(--table-header-padding-y)',
                                                paddingLeft: headerPad ?? 'var(--table-header-padding-x)',
                                                paddingRight: headerPad ?? 'var(--table-header-padding-x)',
                                                width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined 
                                            }}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                        )
                                    })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className={cn(
                                        "hover:bg-table-row-hover border-b border-table-border",
                                        onRowClick && "cursor-pointer",
                                        getRowClassName?.(row.original)
                                    )}
                                    onClick={(e) => {
                                        // Only trigger if clicking directly on the row, not on interactive elements
                                        if (onRowClick && (e.target as HTMLElement).closest('button, a, [role="button"]') === null) {
                                            onRowClick(row.original)
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const meta = (cell.column.columnDef as { meta?: { compact?: boolean } }).meta
                                        const compact = meta?.compact === true
                                        const pad = compact ? 4 : undefined
                                        return (
                                        <TableCell 
                                            key={cell.id} 
                                            className={getCellClassName?.(row.original, cell.column.id) ?? ''}
                                            style={{ 
                                                paddingTop: pad ?? 'var(--table-cell-padding-y)',
                                                paddingBottom: pad ?? 'var(--table-cell-padding-y)',
                                                paddingLeft: pad ?? 'var(--table-cell-padding-x)',
                                                paddingRight: pad ?? 'var(--table-cell-padding-x)',
                                                width: cell.column.getSize() !== 150 ? `${cell.column.getSize()}px` : undefined 
                                            }}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                        )
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    {translations.noResults}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {!disablePagination && (
                <div className="flex items-center justify-between space-x-2 py-4">
                    <div className="flex-1 text-sm text-muted-foreground">
                        {pagination ? (
                            `Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} entries`
                        ) : enableRowSelection ? (
                            `${table.getFilteredSelectedRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} row(s) selected.`
                        ) : null}
                    </div>
                    <div className="flex items-center space-x-6 lg:space-x-8">
                        <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium">{translations.rowsPerPage}</p>
                            <select
                                value={pagination ? pagination.limit : table.getState().pagination.pageSize}
                                onChange={(e) => {
                                    const newLimit = Number(e.target.value)
                                    if (pagination) {
                                        pagination.onPageSizeChange(newLimit)
                                    } else {
                                        table.setPageSize(newLimit)
                                    }
                                }}
                                className="h-8 w-[70px] rounded border border-input bg-background px-3 py-1 text-sm"
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                            {pagination ? (
                                `${translations.page} ${pagination.page} ${translations.of} ${Math.ceil(pagination.total / pagination.limit)}`
                            ) : (
                                `${translations.page} ${table.getState().pagination.pageIndex + 1} ${translations.of} ${table.getPageCount()}`
                            )}
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                className="hidden h-8 w-8 p-0 lg:flex"
                                onClick={() => {
                                    if (pagination) {
                                        pagination.onPageChange(1)
                                    } else {
                                        table.setPageIndex(0)
                                    }
                                }}
                                disabled={pagination ? pagination.page <= 1 : !table.getCanPreviousPage()}
                            >
                                <span className="sr-only">{translations.goToFirstPage}</span>
                                {'<<'}
                            </Button>
                            <Button
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                    if (pagination) {
                                        pagination.onPageChange(pagination.page - 1)
                                    } else {
                                        table.previousPage()
                                    }
                                }}
                                disabled={pagination ? pagination.page <= 1 : !table.getCanPreviousPage()}
                            >
                                <span className="sr-only">{translations.goToPreviousPage}</span>
                                {'<'}
                            </Button>
                            <Button
                                variant="outline"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                    if (pagination) {
                                        pagination.onPageChange(pagination.page + 1)
                                    } else {
                                        table.nextPage()
                                    }
                                }}
                                disabled={pagination ? pagination.page >= Math.ceil(pagination.total / pagination.limit) : !table.getCanNextPage()}
                            >
                                <span className="sr-only">{translations.goToNextPage}</span>
                                {'>'}
                            </Button>
                            <Button
                                variant="outline"
                                className="hidden h-8 w-8 p-0 lg:flex"
                                onClick={() => {
                                    if (pagination) {
                                        pagination.onPageChange(Math.ceil(pagination.total / pagination.limit))
                                    } else {
                                        table.setPageIndex(table.getPageCount() - 1)
                                    }
                                }}
                                disabled={pagination ? pagination.page >= Math.ceil(pagination.total / pagination.limit) : !table.getCanNextPage()}
                            >
                                <span className="sr-only">{translations.goToLastPage}</span>
                                {'>>'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}


